import React, { useCallback, useMemo } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';

import TextInput from 'src/components/TextInput';
import ConfirmModal from 'src/components/ConfirmModal';
import { PencilAdornment } from 'src/utils/components';
import { UserRecordType } from 'src/pages/TimeTrackingPage/api';

import messages from './messages';

export const DECIMALS = 2;
export const WORKED_HOURS_WARNING = 12.5;
export const WORKED_MINUTES_LIMIT = WORKED_HOURS_WARNING * 60;

interface PaymentSummaryProps {
  open: boolean;
  onClose: () => void;
  paymentRecord: UCM.CandidateJobShiftType | null;
  tab: string;
  onChange: (
    paymentRecord: UCM.CandidateJobShiftType,
    nextRecord: Partial<UserRecordType>,
  ) => void;
  localRecord: UserRecordType | null;
}

interface PaymentState {
  wage: string;
  price: string;
  wageBonus: string;
  priceBonus: string;
  nightHours: string;
  sundayHours: string;
  holidayHours: string;
  billableHours: string;
}

const inputNumberSlors = { htmlInput: { min: 0 } };

export default function PaymentSummaryFormModal({
  open,
  onChange,
  onClose,
  paymentRecord,
  localRecord,
  tab,
}: PaymentSummaryProps) {
  const { formatMessage } = useIntl();

  const initialValues = useMemo(
    () => ({
      wage: getValue(localRecord?.wage, paymentRecord?.wage),
      price: getValue(localRecord?.price, paymentRecord?.price),
      nightHours: getValue(localRecord?.nightHours, paymentRecord?.nightHours),
      wageBonus: getValue(localRecord?.wageBonus, paymentRecord?.wageBonus),
      sundayHours: getValue(
        localRecord?.sundayHours,
        paymentRecord?.sundayHours,
      ),
      holidayHours: getValue(
        localRecord?.holidayHours,
        paymentRecord?.holidayHours,
      ),
      priceBonus: getValue(localRecord?.priceBonus, paymentRecord?.priceBonus),
      billableHours: getValue(
        localRecord?.billableHours,
        paymentRecord?.billableHours,
      ),
    }),
    [localRecord, paymentRecord],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const { values, setFieldValue, resetForm } = formik;

  const onChangeEventValue = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      name: keyof PaymentState,
    ) => {
      if (!paymentRecord) return;

      const { value } = event.target;

      setFieldValue(name, value);
      onChange(paymentRecord, {
        [name]: value,
      });
    },
    [onChange, paymentRecord],
  );

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
    resetForm();
  }, [onClose, resetForm]);

  const isUpdateAvailable = useMemo(
    () => tab !== 'done' && paymentRecord?.uuid,
    [tab, paymentRecord?.uuid],
  );

  return (
    <ConfirmModal
      maxWidth="550px"
      open={open}
      onClose={handleClose}
      title={formatMessage(messages.header)}
      submitTextButton={formatMessage(messages.updateButtonLabel)}
    >
      <TextInput
        label={formatMessage(messages.billableColumnLabel)}
        value={values.billableHours}
        onChange={(event) => onChangeEventValue(event, 'billableHours')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.nightColumnLabel)}
        value={values.nightHours}
        onChange={(event) => onChangeEventValue(event, 'nightHours')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.sundayColumnLabel)}
        value={values.sundayHours}
        onChange={(event) => onChangeEventValue(event, 'sundayHours')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.holidayColumnLabel)}
        value={values.holidayHours}
        onChange={(event) => onChangeEventValue(event, 'holidayHours')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.wageColumnLabel)}
        value={values.wage}
        onChange={(event) => onChangeEventValue(event, 'wage')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.wageBonusColumnLabel)}
        value={values.wageBonus}
        onChange={(event) => onChangeEventValue(event, 'wageBonus')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.priceColumnLabel)}
        value={values.price}
        onChange={(event) => onChangeEventValue(event, 'price')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
      <TextInput
        label={formatMessage(messages.priceBonusColumnLabel)}
        value={values.priceBonus}
        onChange={(event) => onChangeEventValue(event, 'priceBonus')}
        type="number"
        slotProps={inputNumberSlors}
        EndIcon={PencilAdornment}
        disabled={!isUpdateAvailable}
      />
    </ConfirmModal>
  );
}

const getValue = (
  localValue: string | number | undefined,
  recordValue: string | number | undefined,
): string => {
  return String(localValue || recordValue || '0');
};
